import { clsx } from 'clsx'

import type { ProgressProps } from '@buffer-mono/popcorn'
import {
  TriangleAlertIcon,
  Button,
  Card,
  EmptyState,
  Flex,
  Heading,
  InfoIcon,
  Link,
  Notice,
  Progress,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'

import { useSplitEnabled } from '@buffer-mono/features'

import { useAPIClientUsageStatistics } from '../../hooks'
import { FreePlanUpgradeNotice } from '../FreePlanUpgradeNotice'

import styles from './UsagePanel.module.css'

import type { JSX } from 'react'

const DEFAULT_TOOLTIP = 'Usage resets on a rolling basis.'

type UsageLevel = 'normal' | 'warning' | 'critical'

interface UsagePanelProps {
  clientId?: string | null
  tooltipText?: string
  /** Rendered under the cards. Only the caller knows what shares the bucket. */
  footnote?: string
  canUpgradeForMore?: boolean
  onUpgrade?: () => void
}

const formatNumber = (num: number): string => num.toLocaleString('en-US')

const getProgressColor = (
  used: number,
  limit: number,
): ProgressProps['color'] => {
  if (limit === 0) {
    return 'brand'
  }

  const percentage = (used / limit) * 100

  if (percentage >= 90) {
    return 'critical'
  }

  if (percentage >= 80) {
    return 'warning'
  }

  return 'brand'
}

const DEFAULT_LIMITS = {
  last15Minutes: 100,
  lastDay: 500,
  lastMonth: 10000,
}

const getUsageLevel = (used: number, limit: number): UsageLevel => {
  if (limit === 0) {
    return 'normal'
  }

  const percentage = (used / limit) * 100

  if (percentage >= 100) {
    return 'critical'
  }

  if (percentage >= 80) {
    return 'warning'
  }

  return 'normal'
}

interface MetricCardProps {
  label: string
  used: number
  limit: number
}

const MetricCard = ({ label, used, limit }: MetricCardProps): JSX.Element => {
  const clampedUsed = limit > 0 ? Math.min(used, limit) : used
  const level = getUsageLevel(used, limit)
  const isElevated = level === 'warning' || level === 'critical'

  return (
    <Card className={styles.metricCard}>
      <Text
        weight="medium"
        className={styles.metricLabel}
        icon={
          isElevated ? (
            <TriangleAlertIcon
              size="small"
              color={level === 'critical' ? 'critical' : 'warning'}
            />
          ) : undefined
        }
      >
        {label}
      </Text>
      <Flex align="baseline" gap="xs" className={styles.metricValueRow}>
        <Text
          className={clsx(
            styles.metricValue,
            level === 'warning' && styles.metricValueWarning,
            level === 'critical' && styles.metricValueCritical,
          )}
        >
          {formatNumber(clampedUsed)}
        </Text>
        <Text className={styles.metricLimit}>/ {formatNumber(limit)}</Text>
      </Flex>
      {limit > 0 && (
        <Progress
          value={clampedUsed}
          max={limit}
          size="small"
          color={getProgressColor(clampedUsed, limit)}
        />
      )}
    </Card>
  )
}

const getHighestUsageLevel = (
  metrics: Array<{ used: number; limit: number }>,
): UsageLevel => {
  const levels = new Set(
    metrics.map(({ used, limit }) => getUsageLevel(used, limit)),
  )

  if (levels.has('critical')) {
    return 'critical'
  }

  if (levels.has('warning')) {
    return 'warning'
  }

  return 'normal'
}

export const UsagePanel = ({
  clientId,
  tooltipText = DEFAULT_TOOLTIP,
  footnote,
  canUpgradeForMore = false,
  onUpgrade,
}: UsagePanelProps): JSX.Element => {
  const { usageData, hasError, errorMessage, refetch } =
    useAPIClientUsageStatistics(clientId ?? null)
  const { isEnabled: isTemporaryLimitsNoticeEnabled, isReady: isSplitReady } =
    useSplitEnabled('developer-temporary-rate-limits-notice')
  const showTemporaryLimitsNotice =
    isSplitReady && isTemporaryLimitsNoticeEnabled

  const usageLevel = usageData
    ? getHighestUsageLevel([
        {
          used: usageData.last15Minutes.used,
          limit: usageData.last15Minutes.limit,
        },
        { used: usageData.lastDay.used, limit: usageData.lastDay.limit },
        { used: usageData.lastMonth.used, limit: usageData.lastMonth.limit },
      ])
    : 'normal'

  if (hasError) {
    return (
      <div className={styles.container}>
        <Flex align="center" gap="xs" className={styles.header}>
          <Heading as="h2" size="small">
            Usage
          </Heading>

          <Tooltip content={tooltipText}>
            <InfoIcon color="subtle" tabIndex={0} />
          </Tooltip>
        </Flex>
        <EmptyState
          variant="critical"
          size="large"
          className={styles.emptyState}
        >
          <EmptyState.Heading>Unable to load usage data</EmptyState.Heading>
          <EmptyState.Description>
            {errorMessage ||
              'Something went wrong while loading your API usage statistics. Please try again.'}
          </EmptyState.Description>
          <EmptyState.Actions className={styles.errorActions}>
            <Button variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </EmptyState.Actions>
        </EmptyState>
      </div>
    )
  }

  return (
    <div className={clsx(styles.container, !clientId && styles.disabled)}>
      <Flex align="center" gap="xs" className={styles.header}>
        <Heading as="h2" size="small">
          Usage
        </Heading>

        <Tooltip content={tooltipText}>
          <InfoIcon color="subtle" tabIndex={0} />
        </Tooltip>
      </Flex>

      {showTemporaryLimitsNotice && (
        <Notice variant="info" icon={false}>
          <Notice.Text>
            We&apos;re currently exploring adjustments to rate limits. As such
            we&apos;ve made temporary adjustments to the limits currently in
            place.
          </Notice.Text>
        </Notice>
      )}

      <div className={styles.metricsGrid}>
        <MetricCard
          label="Last 15 minutes"
          used={usageData?.last15Minutes.used ?? 0}
          limit={usageData?.last15Minutes.limit ?? DEFAULT_LIMITS.last15Minutes}
        />
        <MetricCard
          label="Last 24 hours"
          used={usageData?.lastDay.used ?? 0}
          limit={usageData?.lastDay.limit ?? DEFAULT_LIMITS.lastDay}
        />
        <MetricCard
          label="Last 30 days"
          used={usageData?.lastMonth.used ?? 0}
          limit={usageData?.lastMonth.limit ?? DEFAULT_LIMITS.lastMonth}
        />
      </div>

      {footnote && (
        <Text size="sm" color="subtle">
          {footnote}
        </Text>
      )}

      {usageLevel === 'normal' && <FreePlanUpgradeNotice />}

      {usageLevel === 'critical' && (
        <Notice variant="error" icon={false}>
          <Notice.Text>
            {canUpgradeForMore ? (
              <>
                <strong>Usage limit reached.</strong>{' '}
                <Link as="button" color="inherit" onClick={onUpgrade}>
                  Upgrade
                </Link>{' '}
                for higher limits and more API keys.
              </>
            ) : (
              'Warning: you reached your API usage limit.'
            )}
          </Notice.Text>
        </Notice>
      )}

      {usageLevel === 'warning' && (
        <Notice variant="warning" icon={false}>
          <Notice.Text>
            {canUpgradeForMore ? (
              <>
                <strong>Approaching usage limit.</strong>{' '}
                <Link as="button" color="inherit" onClick={onUpgrade}>
                  Upgrade
                </Link>{' '}
                for higher limits and more API keys.
              </>
            ) : (
              "Warning: you're approaching your API usage limit."
            )}
          </Notice.Text>
        </Notice>
      )}
    </div>
  )
}
